import { AxiosResponse } from 'axios';
import { getCdnURL } from '@/domains/care/helpers';
import { httpClient } from '@/domains/core/httpClient/httpClient';
import { getAppSetting } from '@/domains/core/settings/appSettings';
import type { Platform } from '@/domains/core/settings/interfaces/Platform';
import { getPlatform } from '@/domains/core/settings/utils';
import type { AIReview, AIReviewFromAPI, ApiReviewer, ArticlePicture, ArticlePictureFromAPI, Picture, Product, Review, ReviewSource } from '@/domains/productDiscovery/ArticleReviews/ArticleReviews.model';
import { buildThumbnailURL, buildURL } from '@/domains/productDiscovery/ArticleReviews/modules/reviewsBlock/picturesCarousel/picturesUtils';
import type { Order } from './hooks/useArticleReviews/useArticleReviews';
interface Response<T> {
  pagination: Pagination;
  content: T;
}
interface Pagination {
  page: number;
  pages: number;
  items: number;
  limit: number;
}
export const fetchReviewsStats = (articleId: number): Promise<any> => httpClient.get(`/api/v2/articles-stats`, {
  baseURL: getAppSetting('MS_API_URL'),
  params: {
    article_id: articleId
  }
}).then(res => ({
  articleId: res.data.content.article_id,
  total: res.data.content.total,
  average: res.data.content.average,
  recommended: res.data.content.recommended,
  distribution: res.data.content.distribution
}));
export const fetchMdScores = (articleId: number, categoryId: number): Promise<any> => httpClient.get(`/api/v3/articles/${articleId}/mds-stats`, {
  baseURL: getAppSetting('MS_API_URL'),
  params: {
    platform: getPlatform().toUpperCase(),
    categoryId
  }
}).then(res => res.data.content.map((mdScore: any) => ({
  name: mdScore.name,
  isTop: mdScore.is_top,
  nbTotal: mdScore.nb_total,
  avgRating: mdScore.avg_rating,
  questionId: mdScore.question_id,
  description: mdScore.description,
  ranking: mdScore.ranking.map((item: any) => ({
    articleId: item.article_id,
    position: item.position,
    mdStats: {
      nbTotal: item.md_stats.nb_total,
      avgRating: item.md_stats.avg_rating
    }
  })),
  hasRanking: false,
  rankingPosition: null
})));
interface ApiReview {
  id: number;
  customer_name: string;
  title: string;
  message: string;
  rating: number;
  platform: string;
  created_at: string;
  original_rating_id: number | null;
  pictures: {
    uuid: string;
  }[];
  total_votes: number;
  incentivized: boolean;
  b2b?: boolean;
  purchase_date: string | null;
  source: ReviewSource;
  reviewer: ApiReviewer;
}
export const fetchArticleReviews = (articleId: number, limit = 4, page = 1, onlyB2b = false, stars: number | null = null, orderBy: Order = 'createdAt'): Promise<{
  pagination: Pagination;
  reviews: Review[];
}> => httpClient.get<Response<ApiReview[]>>(`/api/v2/article-ratings`, {
  baseURL: getAppSetting('MS_API_URL'),
  params: {
    platform: getPlatform(),
    article_ids: articleId,
    limit,
    page,
    stars: stars ? [stars] : undefined,
    exclude_translate: false,
    [`order_by[${orderBy}]`]: 'DESC',
    // In order to be compliant with back end contract
    onlyB2B: onlyB2b,
    exclude_ratings: false
  }
}).then(res => {
  const {
    pagination,
    content
  } = res.data;
  const reviews = content.map((data): Review => ({
    reviewId: data.id,
    customerName: data.customer_name,
    title: data.title,
    message: data.message,
    rating: data.rating,
    platform: data.platform,
    createdAt: data.created_at,
    isTranslated: data.original_rating_id !== null,
    votes: data.total_votes,
    incentivized: data.incentivized,
    pictures: data.pictures.map((picture): Picture => ({
      uuid: picture.uuid,
      id: picture.uuid,
      url: buildURL(picture.uuid),
      thumbnail: buildThumbnailURL(picture.uuid)
    })),
    isB2b: data.b2b,
    purchaseDate: data.purchase_date,
    source: data.source,
    reviewer: {
      firstName: data.reviewer?.first_name,
      city: data.reviewer?.city,
      country: data.reviewer?.country
    }
  }));
  return {
    reviews,
    pagination
  };
});
export const getTopRanking = async (articleIds: number[]): Promise<Product[]> => httpClient.get(`/api/product-discovery/product-cards`, {
  baseURL: getAppSetting('MS_API_URL'),
  headers: {
    'x-platform': getPlatform().toUpperCase()
  },
  params: {
    article_ids: articleIds.join(','),
    with_sku_count: false,
    with_catalog_attributes: false,
    with_delivery_detail: false,
    with_delivery_options: false,
    is_pro: false
  }
}).then(res => res.data?.content?.map((art: any): Product => ({
  articleId: Number(art.master_product.article_id),
  modelId: Number(art.sku.model_id),
  productId: Number(art.offer.product_id),
  title: art.sku.title,
  image: `${getCdnURL()}${art.sku.media[0].thumbnail_url}`,
  slug: art.sku.slug_article,
  rating: art.sku.rating.average,
  ratingCount: art.sku.rating.total_ratings,
  isFallback: false,
  price: {
    currency: art.offer.price.currency,
    amount: Number(art.offer.price.current_price.amount_with_vat)
  }
})));
export const fetchArticlePictures = (articleId: number) => httpClient.get(`/b2c-advice/api/v2/carousel-pictures`, {
  baseURL: getAppSetting('MS_API_URL'),
  params: {
    platform: getPlatform().toUpperCase(),
    article_id: articleId
  }
}).then((res: AxiosResponse<{
  content: Array<ArticlePictureFromAPI>;
}>): ArticlePicture[] => res.data.content.map(picture => ({
  uuid: picture.uuid,
  id: picture.uuid,
  url: buildURL(picture.uuid),
  thumbnail: buildThumbnailURL(picture.uuid),
  review: {
    reviewId: picture.review.review_id,
    votes: 0,
    customerName: picture.review.customer_name,
    title: picture.review.title || '',
    message: picture.review.message,
    rating: picture.review.rating,
    platform: picture.review.platform,
    createdAt: picture.review.created_at,
    isTranslated: picture.review.original_rating_id !== null,
    incentivized: Boolean(picture.review.incentivized),
    pictures: [],
    purchaseDate: '',
    source: null,
    reviewer: {
      firstName: picture.review.reviewer?.first_name,
      city: picture.review.reviewer?.city,
      country: picture.review.reviewer?.country
    }
  }
})));
interface LatestReviewsResponse {
  latestReviews: Review[];
  bestRatedReviews: Review[];
  total: number;
}
const parseSingleReview = (review: any, platform: Platform): Review => ({
  customerName: review.customer_name,
  reviewId: review.id,
  message: review.message,
  rating: review.rating,
  createdAt: review.created_at,
  platform,
  title: '',
  isTranslated: false,
  incentivized: review.incentivized,
  votes: review.total_votes || 0,
  pictures: [],
  isB2b: review.is_b2b,
  purchaseDate: review.purchase_date,
  source: review.source,
  reviewer: {
    firstName: review.reviewer?.first_name,
    city: review.reviewer?.city,
    country: review.reviewer?.country
  }
});
export const fetchMainReviews = (articleId: number): Promise<LatestReviewsResponse> => httpClient.get(`/api/v1/latest-reviews`, {
  baseURL: getAppSetting('MS_API_URL'),
  params: {
    platform: getPlatform().toUpperCase(),
    article_id: articleId
  }
}).then(res => {
  const platform = res.data.content.platform_id;
  const latestReviews = res.data.content.latest_reviews.map((rev: any) => parseSingleReview(rev, platform));
  const bestRatedReviews = res.data.content.best_rated_reviews.map((rev: any) => parseSingleReview(rev, platform));
  const total = res.data.content.total_review_count;
  return {
    bestRatedReviews,
    latestReviews,
    total
  };
});
export const voteReviewAsUseful = (reviewId: number): Promise<any> => httpClient.post(`${getAppSetting('MS_API_URL')}/b2c-advice/api/v1/useful-reviews/${reviewId}/vote`);
export const undoVoteReviewAsUseful = (reviewId: number): Promise<any> => httpClient.delete(`/b2c-advice/api/v1/useful-reviews/${reviewId}/vote`, {
  baseURL: getAppSetting('MS_API_URL')
});
const parseAIReview = (apiAIReview: AIReviewFromAPI) => ({
  id: apiAIReview.id,
  totalReviews: apiAIReview.total_reviews,
  link: apiAIReview.link,
  reviewSummary: apiAIReview.review_summary,
  goodPoints: apiAIReview.good_points,
  badPoints: apiAIReview.bad_points,
  collectTime: apiAIReview.collect_time
});
export const fetchAIReview = (articleId: number): Promise<AIReview> => httpClient.get(`/b2c-advice/api/v1/generated-reviews`, {
  baseURL: getAppSetting('MS_API_URL'),
  params: {
    platform: getPlatform().toUpperCase(),
    article_id: articleId
  }
}).then((res): AIReviewFromAPI => res.data.content).then(apiAIReview => parseAIReview(apiAIReview));